import type { AnalyzeSession } from "./ImageAnalyzeDb";
import AnalyzeImageFileStore from "../../services/helpers/AnalyzeImageFileStore";
import { SESSION_IMAGE_ANALYZE } from "../../constants/SessionKeys";

/**
 * Keeps a curator's photo-analysis sessions across a reload.
 *
 * Worth persisting because the state is expensive in a way most UI state is
 * not: every analysed photo cost a vision-model call and a permanent Supabase
 * write, and neither is recoverable by re-rendering. Losing that to a stray
 * refresh means paying for it again.
 *
 * ## What survives, and what honestly cannot
 *
 * Everything in the slice is plain JSON, so drafts, statuses, confidences and
 * `publicUrl`s all come back intact — an analysed tile keeps its thumbnail
 * because the photo is already in storage and the URL points at it.
 *
 * The picked `File` bytes do not come back. They live in AnalyzeImageFileStore,
 * a module-level Map that dies with the page, and there is no way to put a File
 * into sessionStorage. So a photo that had NOT been analysed yet has nothing
 * left to send, and the rehydrate reducer marks it `orphaned` rather than
 * offering a retry button that could only ever fail. Storing the bytes properly
 * would mean IndexedDB; that is a deliberate non-goal, because the photos worth
 * keeping are exactly the ones already uploaded.
 *
 * ## sessionStorage, not localStorage
 *
 * Per-tab is the correct scope here, for two reasons. The file store is also
 * per-tab, so a session restored into a different tab would be all orphans
 * anyway. And two tabs open on the same curator's work would otherwise fight
 * over one key, each overwriting the other's sessions with its own.
 *
 * The cost is that closing the tab discards it — which is the right trade: a
 * session that outlives the tab has no live bytes in it either way.
 *
 * ## Failure is always silent
 *
 * Persistence is a nicety layered under a feature that works without it.
 * Private-mode browsers throw on `sessionStorage` access, quotas get hit, and a
 * stored blob from an older build may not match today's shape. Every one of
 * those paths degrades to "start fresh" rather than surfacing an error, because
 * none of them is something a curator can act on.
 */

/** Bumped whenever the persisted shape changes. A mismatch drops the blob
 *  rather than trying to migrate it — the data is one work session old at most,
 *  and a half-migrated draft is worse than a re-run. */
const SCHEMA_VERSION = 1;

/** Sessions older than this are dropped on load. Long enough to cover a normal
 *  working day of one curator's queue, short enough that a stale blob can't
 *  outlive the storage objects it points at (those are TTL-swept server-side). */
const MAX_SESSION_AGE_MS = 12 * 60 * 60 * 1000;

/** Writes are debounced because the review screen dispatches one action per
 *  keystroke — serialising every session on every letter would be the most
 *  expensive thing on that screen. */
const WRITE_DEBOUNCE_MS = 400;

interface PersistedBlob {
  version: number;
  sessions: Record<string, AnalyzeSession>;
}

/** Structural, so this module never imports the store and can't join an import
 *  cycle with it. */
interface StoreLike {
  getState: () => { imageAnalyze: { sessions: Record<string, AnalyzeSession> } };
  subscribe: (listener: () => void) => unknown;
}

/** sessionStorage access itself throws in some privacy modes — not just the
 *  read, the property lookup. Everything goes through here. */
const storage = (): Storage | null => {
  try {
    return typeof window !== "undefined" && window.sessionStorage
      ? window.sessionStorage
      : null;
  } catch {
    return null;
  }
};

/**
 * Shape check, not a type assertion.
 *
 * The blob is user-reachable (devtools, an older build, a half-written value
 * from a killed tab) and gets spread straight into Redux, so a session that
 * doesn't look right is dropped instead of trusted. Only the fields the
 * reducers actually index into are checked — that is what stops a crash, and
 * validating every leaf would just be a second copy of the type.
 */
const looksLikeSession = (value: unknown): value is AnalyzeSession => {
  if (!value || typeof value !== "object") return false;
  const session = value as Record<string, unknown>;
  return (
    typeof session.setupId === "string" &&
    !!session.images &&
    typeof session.images === "object" &&
    Array.isArray(session.imageOrder) &&
    Array.isArray(session.batches) &&
    !!session.products &&
    typeof session.products === "object" &&
    Array.isArray(session.productOrder) &&
    typeof session.createdAt === "number"
  );
};

/**
 * Reads the stored sessions, or an empty map if anything at all is wrong.
 *
 * Does NOT mark orphans or fix up batch statuses — that is the rehydrate
 * reducer's job, so the rules for what counts as orphaned live in exactly one
 * place next to the rest of the state logic, rather than being reimplemented
 * here where they could quietly drift.
 */
export function loadPersistedAnalyzeSessions(): Record<string, AnalyzeSession> {
  const store = storage();
  if (!store) return {};

  try {
    const raw = store.getItem(SESSION_IMAGE_ANALYZE);
    if (!raw) return {};

    const blob = JSON.parse(raw) as PersistedBlob;
    if (!blob || blob.version !== SCHEMA_VERSION || !blob.sessions) {
      store.removeItem(SESSION_IMAGE_ANALYZE);
      return {};
    }

    const cutoff = Date.now() - MAX_SESSION_AGE_MS;
    const sessions: Record<string, AnalyzeSession> = {};

    Object.entries(blob.sessions).forEach(([setupId, session]) => {
      if (!looksLikeSession(session)) return;
      if (session.createdAt < cutoff) return;
      sessions[setupId] = session;
    });

    return sessions;
  } catch {
    // Corrupt JSON, or a shape from a build that no longer exists. Clearing it
    // means the next reload starts clean instead of failing the same way again.
    try {
      store.removeItem(SESSION_IMAGE_ANALYZE);
    } catch {
      /* nothing to do — the read already failed */
    }
    return {};
  }
}

/** Drops the oldest session and reports whether there was one to drop. Used
 *  only to claw back room after a quota error. */
const evictOldest = (sessions: Record<string, AnalyzeSession>): boolean => {
  const entries = Object.entries(sessions);
  if (entries.length === 0) return false;
  const oldest = entries.reduce((a, b) =>
    a[1].createdAt <= b[1].createdAt ? a : b
  );
  delete sessions[oldest[0]];
  return true;
};

const write = (sessions: Record<string, AnalyzeSession>): void => {
  const store = storage();
  if (!store) return;

  // Nothing to keep — remove the key rather than storing an empty object, so
  // "Start over" actually leaves no trace.
  if (Object.keys(sessions).length === 0) {
    try {
      store.removeItem(SESSION_IMAGE_ANALYZE);
    } catch {
      /* ignore */
    }
    return;
  }

  // Copy before any eviction: this is Redux state, and dropping a session out
  // of the live store to satisfy a storage quota would be a spectacular way to
  // lose a curator's work.
  const payload: Record<string, AnalyzeSession> = { ...sessions };

  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      store.setItem(
        SESSION_IMAGE_ANALYZE,
        JSON.stringify({ version: SCHEMA_VERSION, sessions: payload })
      );
      return;
    } catch {
      // Almost always QuotaExceededError. Shed the oldest session and retry;
      // if there is nothing left to shed, give up quietly.
      if (!evictOldest(payload)) return;
    }
  }
};

/**
 * Drops any File whose session no longer references it.
 *
 * The file store is the one piece of this feature's state that lives outside
 * Redux, so it cannot be wiped by a reducer — and `URL.createObjectURL` hands
 * out a blob reference that survives for the life of the document unless it is
 * explicitly revoked. Components already call `remove`/`removeMany` on every
 * path a curator can take (removing a photo, "Start over", cleanup after a
 * successful create), but there is one exit they cannot cover: logout
 * dispatches `resetAllState`, the slice resets to `initialState`, and no
 * component is mounted to clean up behind it. Those Files and blob URLs would
 * then be unreachable and unreleased for the rest of the tab's life.
 *
 * Reconciling here rather than at the logout call site fixes that without
 * teaching a generic sign-out utility about this feature, and without putting a
 * side effect inside a reducer — reducers stay pure, which is also what keeps
 * them safe to re-run under the devtools. It makes the invariant explicit
 * ("the store holds exactly the images the live sessions reference") and
 * self-healing, so the explicit calls become belt-and-braces rather than the
 * only thing standing between this feature and a leak.
 */
const reconcileFileStore = (sessions: Record<string, AnalyzeSession>): void => {
  const live = new Set<string>();
  Object.values(sessions).forEach((session) => {
    session.imageOrder.forEach((imageId) => live.add(imageId));
  });

  const orphans = AnalyzeImageFileStore.ids().filter((id) => !live.has(id));
  if (orphans.length > 0) {
    AnalyzeImageFileStore.removeMany(orphans);
  }
};

/** Guards against a second subscription if this module is ever evaluated twice
 *  — a dev-server hot update that re-runs the store module would otherwise
 *  leave two listeners writing the same key. */
let started = false;

/**
 * Keeps everything that lives outside the slice in step with it: mirrors
 * sessions into storage, and releases Files the sessions no longer reference.
 *
 * Subscribes once, at store creation, and does nothing unless the `sessions`
 * reference actually changes — Immer hands back a new reference exactly when
 * something underneath it changed, so this skips every action from every other
 * slice in the app without inspecting any of them.
 *
 * The two duties are paced differently on purpose. Releasing Files runs
 * immediately, because a revoked blob URL is memory handed back and there is no
 * reason to hold it for another 400ms. The storage write is debounced, because
 * the review screen dispatches one action per keystroke and serialising every
 * session on each letter would be the most expensive thing on that screen.
 */
export function startAnalyzeSessionSync(store: StoreLike): void {
  if (started) return;
  started = true;

  let lastSessions = store.getState().imageAnalyze.sessions;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const canPersist = !!storage();

  store.subscribe(() => {
    const sessions = store.getState().imageAnalyze.sessions;
    if (sessions === lastSessions) return;
    lastSessions = sessions;

    reconcileFileStore(sessions);

    // Storage being unavailable (private browsing) must not stop the file
    // reconciliation above, which is the part that prevents a real leak.
    if (!canPersist) return;

    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      write(lastSessions);
    }, WRITE_DEBOUNCE_MS);
  });
}
